import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { MinusOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { GoodsProps } from '../../api/Goods';
import * as GoodsApi from '../../api/Goods';
import * as GoodsTagsApi from '../../api/GoodsTags';

const { Text } = Typography;

type ComboRow = { article: string; value: number };
type TagValue = { id?: number; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  data: GoodsProps;
  brands: { name: string }[];
  onSaved: (next: GoodsProps) => void;
  focusCombo?: boolean;
};

const goodsLabel = (g: {
  article: string;
  name?: string;
  brand?: { name?: string } | null;
}) => {
  const brand = g.brand?.name ? `${g.brand.name} · ` : '';
  return `${g.article} — ${brand}${g.name || ''}`;
};

export const ProductEditDrawer = ({
  open,
  onClose,
  data,
  brands,
  onSaved,
  focusCombo,
}: Props) => {
  const [name, setName] = useState(data.name);
  const [brandName, setBrandName] = useState(data.brand?.name || undefined);
  const [categoryId, setCategoryId] = useState<number | undefined>(
    data.category?.id,
  );
  const [categories, setCategories] = useState<
    { id: number; name: string }[]
  >([]);
  const [availableTags, setAvailableTags] = useState<
    { id: number; name: string }[]
  >([]);
  const [tags, setTags] = useState<TagValue[]>(data.tags || []);
  const [fbs, setFbs] = useState(!!data.product_remnants?.show_for_distribution);
  const [isCombo, setIsCombo] = useState(!!data.is_combo);
  const [rows, setRows] = useState<ComboRow[]>([]);
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(data.name || '');
    setBrandName(data.brand?.name || undefined);
    setCategoryId(data.category?.id);
    setTags(data.tags || []);
    setFbs(!!data.product_remnants?.show_for_distribution);
    setIsCombo(!!data.is_combo);
    setRows(
      (data.comboComposition || []).map((r) => ({
        article: r.article,
        value: Math.max(1, Number(r.value) || 1),
      })),
    );
    void GoodsApi.getCategories()
      .then((list) =>
        setCategories(
          [...(list || [])].sort((a, b) =>
            a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
          ),
        ),
      )
      .catch(() => setCategories([]));
    void GoodsTagsApi.getAll()
      .then((list) =>
        setAvailableTags(
          (list || []).map((t) => ({ id: t.id, name: t.name })),
        ),
      )
      .catch(() => setAvailableTags([]));
  }, [open, data]);

  const brandOptions = useMemo(
    () => brands.map((b) => ({ value: b.name, label: b.name })),
    [brands],
  );

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.name })),
    [categories],
  );

  const tagOptions = useMemo(() => {
    const byLower = new Map<string, { value: string; label: string }>();
    for (const t of availableTags) {
      byLower.set(t.name.toLowerCase(), { value: t.name, label: t.name });
    }
    for (const t of tags) {
      const key = t.name.toLowerCase();
      if (!byLower.has(key)) {
        byLower.set(key, { value: t.name, label: t.name });
      }
    }
    return [...byLower.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' }),
    );
  }, [availableTags, tags]);

  const searchGoods = async (q: string) => {
    if (!q.trim()) {
      setOptions([]);
      return;
    }
    const qTrim = q.trim();
    // article+name+alias одинаковые → сервер ищет OR по полям (как в списке товаров)
    const res = await GoodsApi.getAll({
      name: qTrim,
      article: qTrim,
      alias: qTrim,
      limit: 30,
      page: 1,
    });
    const list = Array.isArray(res) ? res : res.data;
    setOptions(
      list
        .filter((g) => g.article !== data.article)
        .map((g) => ({ value: g.article, label: goodsLabel(g) })),
    );
  };

  const save = async () => {
    setPending(true);
    try {
      const next = await GoodsApi.edit(data.article, {
        name: name.trim(),
        brand: brandName ? { name: brandName } : null,
        product_remnants: { show_for_distribution: fbs },
        is_combo: isCombo,
        comboComposition: isCombo
          ? rows
              .filter((r) => r.article)
              .map((r) => ({
                article: r.article,
                value: Math.max(1, Number(r.value) || 1),
              }))
          : [],
        category:
          categoryId == null
            ? null
            : categories.find((c) => c.id === categoryId) ||
              data.category ||
              null,
        tags: tags
          .map((t) => {
            const trimmed = t.name.trim();
            if (!trimmed) return null;
            const existing = availableTags.find(
              (a) => a.name.toLowerCase() === trimmed.toLowerCase(),
            );
            if (existing) return { id: existing.id, name: existing.name };
            if (t.id) return { id: t.id, name: trimmed };
            return { name: trimmed };
          })
          .filter(Boolean) as TagValue[],
      });
      message.success('Сохранено');
      onSaved(next);
      onClose();
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Drawer
      title={`Редактирование · ${data.article}`}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" loading={pending} onClick={save}>
            Сохранить
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Артикул">
          <Input value={data.article} disabled />
        </Form.Item>
        <Form.Item
          label="Название"
          required
          validateStatus={!name.trim() ? 'warning' : undefined}
          help={!name.trim() ? 'Не заполнено' : undefined}
        >
          <Input
            autoFocus={!focusCombo}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Не заполнено"
          />
        </Form.Item>
        <Form.Item
          label="Бренд"
          validateStatus={!brandName ? 'warning' : undefined}
          help={!brandName ? 'Не заполнено' : undefined}
        >
          <Select
            showSearch
            allowClear
            placeholder="Не заполнено"
            value={brandName}
            options={brandOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={setBrandName}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Категория">
          <Select
            showSearch
            allowClear
            placeholder="Не заполнено"
            value={categoryId}
            options={categoryOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLocaleLowerCase('ru')
                .includes(input.trim().toLocaleLowerCase('ru'))
            }
            onChange={(id: number | undefined) => setCategoryId(id)}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Теги">
          <Select
            mode="tags"
            allowClear
            showSearch
            placeholder="Выбрать или создать"
            style={{ width: '100%' }}
            value={tags.map((t) => t.name)}
            options={tagOptions}
            tokenSeparators={[',']}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLocaleLowerCase('ru')
                .includes(input.trim().toLocaleLowerCase('ru'))
            }
            onChange={(values: string[]) => {
              setTags(
                values
                  .map((v) => v.trim())
                  .filter(Boolean)
                  .map((tagName) => {
                    const existing = availableTags.find(
                      (a) => a.name.toLowerCase() === tagName.toLowerCase(),
                    );
                    return existing
                      ? { id: existing.id, name: existing.name }
                      : { name: tagName };
                  }),
              );
            }}
          />
        </Form.Item>
        <Form.Item label="Включено в FBS / распределение">
          <Switch checked={fbs} onChange={setFbs} />
        </Form.Item>
        <Form.Item label="Комбо-товар">
          <Switch checked={isCombo} onChange={setIsCombo} />
        </Form.Item>
        {isCombo && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Состав комбо
            </Text>
            <Table
              size="small"
              pagination={false}
              rowKey={(_, i) => String(i)}
              dataSource={rows}
              columns={[
                {
                  title: 'Товар',
                  render: (_, row, index) => (
                    <Select
                      showSearch
                      style={{ width: '100%', minWidth: 180 }}
                      placeholder="Артикул / название"
                      value={row.article || undefined}
                      options={options}
                      filterOption={false}
                      onSearch={(q) => void searchGoods(q)}
                      onChange={(article) =>
                        setRows((prev) =>
                          prev.map((r, i) =>
                            i === index ? { ...r, article } : r,
                          ),
                        )
                      }
                    />
                  ),
                },
                {
                  title: 'Кол-во',
                  width: 140,
                  render: (_, row, index) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        icon={<MinusOutlined />}
                        disabled={row.value <= 1}
                        onClick={() =>
                          setRows((prev) =>
                            prev.map((r, i) =>
                              i === index
                                ? { ...r, value: Math.max(1, r.value - 1) }
                                : r,
                            ),
                          )
                        }
                      />
                      <InputNumber
                        min={1}
                        size="small"
                        value={row.value}
                        onChange={(v) =>
                          setRows((prev) =>
                            prev.map((r, i) =>
                              i === index
                                ? { ...r, value: Math.max(1, Number(v) || 1) }
                                : r,
                            ),
                          )
                        }
                      />
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() =>
                          setRows((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, value: r.value + 1 } : r,
                            ),
                          )
                        }
                      />
                    </Space>
                  ),
                },
                {
                  title: '',
                  width: 40,
                  render: (_, __, index) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setRows((prev) => prev.filter((_, i) => i !== index))
                      }
                    />
                  ),
                },
              ]}
            />
            <Button
              style={{ marginTop: 8 }}
              onClick={() =>
                setRows((prev) => [...prev, { article: '', value: 1 }])
              }
            >
              Добавить строку
            </Button>
          </>
        )}
      </Form>
    </Drawer>
  );
};
